import { useEffect, useCallback } from 'react';
import { useUIStore } from '../store';

interface KeyboardShortcutsOptions {
  onOpenProject?: () => void;
  onExport?: () => void;
  onLoadPhotos?: () => void;
  onSaveAnomaly?: () => void;
  onFitToView?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
}

/**
 * Hook for handling keyboard shortcuts throughout the app
 * 
 * Shortcuts:
 * - Ctrl+O: Open project (load JSON)
 * - Ctrl+I: Load photos
 * - Ctrl+S: Save current anomaly
 * - Ctrl+E: Export JSON
 * - Space: Fit photo to screen
 * - +/-: Zoom in/out
 * - P: Point annotation mode
 * - G: Polygon annotation mode
 * - 1/2/3: Select severity level (in form)
 * - Escape: Cancel current operation
 * - Enter: Finish polygon / confirm
 * - Backspace: Undo last point (in polygon mode)
 * - Arrow keys: Pan photo
 * - N: Next photo
 * - B: Previous photo
 */
export function useKeyboardShortcuts(options: KeyboardShortcutsOptions = {}) {
  const {
    currentView,
    annotationMode,
    setAnnotationMode,
    undoLastPoint,
    clearPendingAnnotation,
    pendingAnnotationPoints,
    closeAnomalyForm,
    isAnomalyFormOpen,
  } = useUIStore();

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const target = event.target as HTMLElement;
    const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
    
    // Don't handle shortcuts when typing in inputs (except Escape)
    if (isInput && event.key !== 'Escape') {
      return;
    }

    const { ctrlKey, metaKey, key } = event;
    const isCtrl = ctrlKey || metaKey;

    // Ctrl/Cmd shortcuts
    if (isCtrl) {
      switch (key.toLowerCase()) {
        case 'o':
          event.preventDefault();
          options.onOpenProject?.();
          break;
        case 'i':
          event.preventDefault();
          options.onLoadPhotos?.();
          break;
        case 's':
          event.preventDefault();
          options.onSaveAnomaly?.();
          break;
        case 'e':
          event.preventDefault();
          options.onExport?.();
          break;
      }
      return;
    }

    // Non-modifier shortcuts (only when not in form)
    if (!isAnomalyFormOpen) {
      switch (key) {
        // Annotation modes
        case 'p':
        case 'P':
          event.preventDefault();
          setAnnotationMode(annotationMode === 'point' ? null : 'point');
          break;
        case 'g':
        case 'G':
          event.preventDefault();
          setAnnotationMode(annotationMode === 'polygon' ? null : 'polygon');
          break;

        // Zoom
        case '+':
        case '=':
          event.preventDefault();
          options.onZoomIn?.();
          break;
        case '-':
        case '_':
          event.preventDefault();
          options.onZoomOut?.();
          break;

        // Fit to view
        case ' ':
          if (currentView === 'photo') {
            event.preventDefault();
            options.onFitToView?.();
          }
          break;

        // Polygon mode controls
        case 'Backspace':
          if (annotationMode === 'polygon' && pendingAnnotationPoints.length > 0) {
            event.preventDefault();
            undoLastPoint();
          }
          break;

        case 'Enter':
          // Finish polygon is handled by PhotoViewer
          break;
      }
    }

    // Always handle Escape
    if (key === 'Escape') {
      event.preventDefault();
      if (isAnomalyFormOpen) {
        closeAnomalyForm();
      } else if (annotationMode) {
        clearPendingAnnotation();
      }
    }
  }, [
    currentView,
    annotationMode,
    setAnnotationMode,
    undoLastPoint,
    clearPendingAnnotation,
    pendingAnnotationPoints,
    closeAnomalyForm,
    isAnomalyFormOpen,
    options
  ]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}

/**
 * Hook specifically for the PhotoViewer component
 */
export function usePhotoViewerShortcuts(callbacks: {
  onFitToView: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetView: () => void;
  onFinishPolygon: () => void;
  onPrevPhoto?: () => void;
  onNextPhoto?: () => void;
  onShowOnMap?: () => void;
}) {
  const {
    annotationMode,
    pendingAnnotationPoints,
    setAnnotationMode,
    undoLastPoint,
    clearPendingAnnotation,
  } = useUIStore();

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const target = event.target as HTMLElement;
    const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
    
    if (isInput) return;

    switch (event.key) {
      case ' ':
        event.preventDefault();
        callbacks.onFitToView();
        break;
      case '+':
      case '=':
        event.preventDefault();
        callbacks.onZoomIn();
        break;
      case '-':
        event.preventDefault();
        callbacks.onZoomOut();
        break;
      case 'r':
      case 'R':
        event.preventDefault();
        callbacks.onResetView();
        break;
      case 'h':
      case 'H':
        event.preventDefault();
        setAnnotationMode(null); // Pan mode
        break;
      case 'p':
      case 'P':
        event.preventDefault();
        setAnnotationMode('point');
        break;
      case 'b':
      case 'B':
        event.preventDefault();
        setAnnotationMode('rectangle');
        break;
      case 'g':
      case 'G':
        event.preventDefault();
        setAnnotationMode('polygon');
        break;
      // Photo navigation
      case 'n':
      case 'N':
      case 'ArrowRight':
        event.preventDefault();
        callbacks.onNextPhoto?.();
        break;
      case 'b':
      case 'B':
      case 'ArrowLeft':
        event.preventDefault();
        callbacks.onPrevPhoto?.();
        break;
      // Show on map
      case 'm':
      case 'M':
        event.preventDefault();
        callbacks.onShowOnMap?.();
        break;
      case 'Backspace':
        if (annotationMode === 'polygon' && pendingAnnotationPoints.length > 0) {
          event.preventDefault();
          undoLastPoint();
        }
        break;
      case 'Enter':
        if (annotationMode === 'polygon' && pendingAnnotationPoints.length >= 3) {
          event.preventDefault();
          callbacks.onFinishPolygon();
        }
        break;
      case 'Escape':
        event.preventDefault();
        if (annotationMode) {
          clearPendingAnnotation();
        }
        break;
    }
  }, [
    annotationMode,
    pendingAnnotationPoints,
    setAnnotationMode,
    undoLastPoint,
    clearPendingAnnotation,
    callbacks
  ]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}
